import BigNumber from "bignumber.js";
import {
  IExactPayOperation,
  ISwapPathInfo,
  quoteExactPayPrice,
  SwapRequest,
} from "../../../../api/swap";
import { useChains } from "../../../../components/FetchConfig";
import { useAlerting, useTranslation } from "../../../../hooks";
import { useAppSelector } from "../../../../reducer/hooks";
import { selectProfile } from "../../../../reducer/profileSlice";
import {
  amountToCryptoAmount,
  findDecimalByChainNameAndAsset,
} from "../../../../utils/helper";
import { toQuoteDetailsAmount } from "../Content/components/QuoteDetails";
import { getApiSwapPath } from "../Content/helpers/getApiSwapPath";
import { useCreateSwapRequestContext } from "../context";
import { TabTypes } from "../types";
import { useQuote } from "./useQuote";
import { getCommonDisplayDecimals } from "../helpers/getCommonDisplayDecimals";

interface IProps {
  swapPaths: ISwapPathInfo[];
}

export const useExactPayQuote = (props: IProps) => {
  const { swapPaths } = props;

  const { filterFields, exactPayFields, setExactPayFields } =
    useCreateSwapRequestContext();

  const { merchantId } = useAppSelector(selectProfile);
  const { selectChainByPieces } = useChains();

  const { chain_type, chain_id } = selectChainByPieces({
    chain_name: filterFields.chainName,
  });

  const fromCurrencyDecimals =
    Number(
      findDecimalByChainNameAndAsset(
        String(chain_type),
        String(chain_id),
        filterFields.fromCurrency
      )
    ) || 6;

  const toCurrencyDecimals =
    Number(
      findDecimalByChainNameAndAsset(
        String(chain_type),
        String(chain_id),
        filterFields.toCurrency
      )
    ) || 6;

  const { t } = useTranslation("createSwapRequest");
  const { alerting } = useAlerting();

  const quotePayload: SwapRequest<IExactPayOperation> = {
    merchant_id: Number(merchantId),
    chain_type: Number(chain_type),
    chain_id: Number(chain_id),
    asset_name: filterFields.fromCurrency,
    asset_name_get: filterFields.toCurrency,
    operations: [
      {
        operation_seq: "1",
        amount: amountToCryptoAmount(
          BigNumber(exactPayFields.exactPayAmount || "")
            .decimalPlaces(getCommonDisplayDecimals(fromCurrencyDecimals), 1)
            .toFixed(),
          fromCurrencyDecimals
        ),
        decimals: fromCurrencyDecimals,
        swap_path: getApiSwapPath(
          swapPaths,
          filterFields.fromCurrency,
          filterFields.toCurrency
        ),
      },
    ],
  };

  const validateExactPayQuote = () => {
    if (!filterFields.fromCurrency || !filterFields.toCurrency) {
      alerting("warning", t("psCurrencyPair"));
      return false;
    }

    if (!exactPayFields.exactPayAmount) {
      alerting("warning", t("peExactPayAmount"));
      return false;
    }

    if (
      quotePayload.operations[0].swap_path &&
      quotePayload.operations[0].swap_path.length === 0
    ) {
      alerting("warning", t("noSwapPathFound"));
      return false;
    }

    return true;
  };

  const { handleQuote } = useQuote({
    fields: exactPayFields,
    setFields: setExactPayFields,
    mode: TabTypes.EXACT_PAY,
    payload: quotePayload,
    quoteApiFn: quoteExactPayPrice,
    validateFunc: validateExactPayQuote,
  });

  return { handleQuote };
};
